import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { NotificationContext, NotificationContextType } from './NotificationContext';
import { Notification, NotificationState, NotificationFilter } from '@/types/notification';
import { useSupabase } from '@/contexts/supabase/SupabaseClientProvider';
import { useAuth } from '@/hooks/use-auth';

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const { user } = useAuth();
  const supabase = useSupabase();
  const [state, setState] = useState<NotificationState>({
    notifications: [],
    unreadCount: 0,
    isLoading: false,
  });

  const mapRow = useCallback((row: any): Notification => ({
    id: row.id,
    type: row.type,
    title: row.title,
    description: row.description,
    timestamp: new Date(row.created_at),
    isRead: row.is_read ?? false,
    userId: row.user_id,
    actionUrl: row.action_url ?? undefined,
    priority: row.priority ?? 'medium',
    expiresAt: row.expires_at ? new Date(row.expires_at) : undefined,
    metadata: row.metadata ?? undefined
  }), []);

  const fetchNotifications = useCallback(async () => {
    const isUuid = typeof user?.id === 'string' && /^[0-9a-fA-F-]{36}$/.test(user.id);
    if (!user || !isUuid) {
      setState({ notifications: [], unreadCount: 0, isLoading: false });
      return;
    }
    setState(prev => ({ ...prev, isLoading: true }));
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      return;
    }

    const notifications = (data || []).map(mapRow);
    const unreadCount = notifications.filter(n => !n.isRead).length;
    setState({ notifications, unreadCount, isLoading: false });
  }, [user, mapRow]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('public:notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        payload => {
          const notification = mapRow(payload.new);
          setState(prev => ({
            ...prev,
            notifications: [notification, ...prev.notifications],
            unreadCount: prev.unreadCount + (notification.isRead ? 0 : 1)
          }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, mapRow]);

  const addNotification = useCallback(async (notificationData: Omit<Notification, 'id' | 'timestamp' | 'isRead'>) => {
    const isUuid = typeof user?.id === 'string' && /^[0-9a-fA-F-]{36}$/.test(user.id);
    if (!user || !isUuid) return;

    // Check database if notification already exists (if notificationId provided)
    if (notificationData.metadata?.notificationId) {
      const { data: existing } = await supabase
        .from('notifications')
        .select('id')
        .eq('user_id', user.id)
        .eq('metadata->>notificationId', notificationData.metadata.notificationId)
        .maybeSingle();
      
      if (existing) {
        return; // Already exists, skip
      }
    }

    // Insert notification, ignore unique constraint violations
    const { error } = await supabase.from('notifications').insert({
      user_id: user.id,
      type: notificationData.type,
      title: notificationData.title,
      description: notificationData.description,
      action_url: notificationData.actionUrl,
      priority: notificationData.priority,
      expires_at: notificationData.expiresAt?.toISOString(),
      metadata: notificationData.metadata
    });

    // Ignore unique constraint violations (23505 = unique_violation)
    if (error && error.code !== '23505') {
      console.error('Failed to create notification:', error);
    }
  }, [user, supabase]);

  const markAsRead = useCallback(async (notificationId: string) => {
    await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', notificationId);

    setState(prev => {
      const updatedNotifications = prev.notifications.map(n =>
        n.id === notificationId ? { ...n, isRead: true } : n
      );
      const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
      return { ...prev, notifications: updatedNotifications, unreadCount };
    });
  }, []);

  const markAllAsRead = useCallback(async () => {
    const isUuid = typeof user?.id === 'string' && /^[0-9a-fA-F-]{36}$/.test(user.id);
    if (!user || !isUuid) return;
    await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('is_read', false);

    setState(prev => ({
      ...prev,
      notifications: prev.notifications.map(n => ({ ...n, isRead: true })),
      unreadCount: 0
    }));
  }, [user]);

  const removeNotification = useCallback(async (notificationId: string) => {
    await supabase.from('notifications').delete().eq('id', notificationId);
    setState(prev => {
      const updatedNotifications = prev.notifications.filter(n => n.id !== notificationId);
      const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
      return { ...prev, notifications: updatedNotifications, unreadCount };
    });
  }, []);

  const clearExpired = useCallback(() => {
    const now = new Date();
    setState(prev => {
      const updatedNotifications = prev.notifications.filter(n =>
        !n.expiresAt || n.expiresAt > now
      );
      const unreadCount = updatedNotifications.filter(n => !n.isRead).length;
      return { ...prev, notifications: updatedNotifications, unreadCount };
    });
  }, []);

  const getFilteredNotifications = useCallback((filter: NotificationFilter): Notification[] => {
    switch (filter) {
      case 'unread':
        return state.notifications.filter(n => !n.isRead);
      case 'event':
      case 'tournament':
      case 'message':
      case 'penalty':
      case 'group':
        return state.notifications.filter(n => n.type === filter);
      default:
        return state.notifications;
    }
  }, [state.notifications]);

  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  const contextValue: NotificationContextType = {
    ...state,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearExpired,
    getFilteredNotifications,
    refreshNotifications
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}
